/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    ADMIN_DASHBOARD_REDESIGN
 * Date:         2026-03-07
 * Modified By:  Claude
 *
 * Description:
 * Redesigned admin dashboard from flat 3-column grid to grouped card layout.
 * Cards organized by function: Configuration, Scheduler Jobs, Data & Storage,
 * Monitoring & Logs, Security & Access. Vertical card layout with colored icon
 * backgrounds, stat pill badges, and planned/disabled state for Future Jobs.
 *
 * File Size Check:
 * - Current: 254 lines
 * - Projected: ~140 lines
 * - Decision: Proceed (significant reduction)
 *
 * Related Files:
 * - AdminDashboardSections.scss (complete rewrite)
 * - docs/output/planning/mockups/admin-dashboard-redesign.html (Maya's mockup)
 */

import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUsers,
  faExchangeAlt,
  faKey,
  faFileAlt,
  faCrown,
  faDesktop,
  faChartBar,
  faDatabase,
  faSlidersH,
  faHdd,
  faCalendarAlt,
  faBan,
  faGlobe,
} from "@fortawesome/free-solid-svg-icons";
import { useNavigate } from "react-router-dom";
import { getAdminStats, AdminStats } from "../../../services/admin/adminStats";
import "./AdminDashboardSections.scss";

interface DashboardCard {
  title: string;
  description: string;
  icon: any;
  iconTheme: "blue" | "green" | "orange" | "red" | "purple" | "teal";
  path: string;
  statsKey?: "logs" | "monitoring" | "analytics";
  isPlanned?: boolean;
}

interface DashboardCategory {
  label: string;
  cards: DashboardCard[];
}

// [2026-03-07] dashboard-redesign: Grouped card structure per Maya's approved mockup
const DASHBOARD_CATEGORIES: DashboardCategory[] = [
  {
    label: "Configuration",
    cards: [
      {
        title: "System Parameters",
        description: "Runtime configuration across all services",
        icon: faSlidersH,
        iconTheme: "teal",
        path: "/admin/system-params",
      },
      {
        title: "Users",
        description: "Manage accounts, roles, and permissions",
        icon: faUsers,
        iconTheme: "purple",
        path: "/admin/users",
      },
      {
        title: "Subscription Settings",
        description: "Configure Basic, Standard, and Pro tiers",
        icon: faCrown,
        iconTheme: "blue",
        path: "/admin/subscriptions",
      },
    ],
  },
  {
    label: "Scheduler Jobs",
    cards: [
      {
        title: "Backup & Restore",
        description: "Automated database backup and restore management",
        icon: faHdd,
        iconTheme: "green",
        path: "/admin/scheduler/backup",
      },
      {
        title: "Exchange Rates",
        description: "Daily currency rate updates for platform trading",
        icon: faExchangeAlt,
        iconTheme: "orange",
        path: "/admin/scheduler/exchange-rates",
      },
      {
        title: "Future Jobs",
        description: "Not yet implemented",
        icon: faCalendarAlt,
        iconTheme: "blue",
        path: "/admin/scheduler/planned-jobs",
        isPlanned: true,
      },
    ],
  },
  {
    label: "Data & Storage",
    cards: [
      {
        title: "Database Statistics",
        description: "MongoDB collection sizes and document counts",
        icon: faDatabase,
        iconTheme: "blue",
        path: "/admin/database-statistics",
      },
    ],
  },
  {
    label: "Monitoring & Logs",
    cards: [
      {
        title: "System Logs",
        description: "Search and analyze application logs in real-time",
        icon: faFileAlt,
        iconTheme: "red",
        path: "/admin/logs",
        statsKey: "logs",
      },
      {
        title: "Monitoring",
        description: "Track transactions and trace operations",
        icon: faDesktop,
        iconTheme: "blue",
        path: "/admin/monitoring",
        statsKey: "monitoring",
      },
      {
        title: "Analytics",
        description: "Insights, charts, and performance metrics",
        icon: faChartBar,
        iconTheme: "purple",
        path: "/admin/analytics",
        statsKey: "analytics",
      },
    ],
  },
  {
    label: "Security & Access",
    cards: [
      {
        title: "Logins / Sessions",
        description: "Monitor user logins and active sessions",
        icon: faKey,
        iconTheme: "orange",
        path: "/admin/logins",
      },
      // [2026-06-26] blocklist: Add Blocklist card — block emails/domains from signup/signin
      {
        title: "Blocklist",
        description: "Block emails or domains from signing up or signing in",
        icon: faBan,
        iconTheme: "red",
        path: "/admin/blocklist",
      },
      // [2026-07-06] GUEST_ACCESS BR-16: anonymous visitor activity + IP/country blocking
      {
        title: "Visitors",
        description: "Anonymous guest activity — view, filter, and block IPs or countries",
        icon: faGlobe,
        iconTheme: "teal",
        path: "/admin/guests",
      },
    ],
  },
];

const getSectionStat = (
  key: DashboardCard["statsKey"],
  stats: AdminStats | null
): { label: string; color: "red" | "blue" | "green" } | undefined => {
  if (!key || !stats) return undefined;

  switch (key) {
    case "logs":
      return { label: `${stats.recentErrors} recent errors`, color: "red" };
    case "monitoring":
      return { label: `${stats.activeTraces} active traces`, color: "blue" };
    case "analytics":
      return { label: `${stats.operationsToday} today`, color: "green" };
    default:
      return undefined;
  }
};

const AdminDashboardSections: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<AdminStats | null>(null);

  useEffect(() => {
    const loadStats = async () => {
      try {
        const data = await getAdminStats();
        setStats(data);
      } catch (error) {
        console.error("Failed to load admin stats:", error);
      }
    };

    loadStats();
    const interval = setInterval(loadStats, 30000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="admin-dashboard-sections">
      {DASHBOARD_CATEGORIES.map((category) => (
        <div key={category.label} className="admin-category">
          <div className="admin-category__label">{category.label}</div>
          <div className="admin-category__grid">
            {category.cards.map((card) => {
              const stat = getSectionStat(card.statsKey, stats);

              return (
                <div
                  key={card.path}
                  className={`admin-card${card.isPlanned ? " admin-card--planned" : ""}`}
                  onClick={() => !card.isPlanned && navigate(card.path)}
                >
                  <div className="admin-card__top">
                    <div className={`admin-card__icon admin-card__icon--${card.iconTheme}`}>
                      <FontAwesomeIcon icon={card.icon} />
                    </div>
                    <span className="admin-card__arrow">&#8594;</span>
                  </div>
                  <div className="admin-card__title">{card.title}</div>
                  <div className="admin-card__desc">{card.description}</div>
                  {stat && (
                    <div className={`admin-card__stat admin-card__stat--${stat.color}`}>
                      {stat.label}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default AdminDashboardSections;
